import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { BioScoreReportData } from '@/services/export/reportDataBuilder';

type PdfDoc = jsPDF & {
	lastAutoTable?: {
		finalY: number;
	};
};

const PAGE = {
	marginX: 40,
	marginY: 48,
	lineHeight: 16,
};

const getNextY = (doc: PdfDoc, fallback: number) => doc.lastAutoTable?.finalY ?? fallback;

const ensureSpace = (doc: jsPDF, y: number, needed = 80) => {
	const pageHeight = doc.internal.pageSize.getHeight();
	if (y + needed > pageHeight - PAGE.marginY) {
		doc.addPage();
		return PAGE.marginY;
	}
	return y;
};

const addSectionTitle = (doc: jsPDF, title: string, y: number) => {
	doc.setFont('helvetica', 'bold');
	doc.setFontSize(13);
	doc.text(title, PAGE.marginX, y);
	return y + 18;
};

const addWrappedText = (doc: jsPDF, text: string, y: number, fontSize = 10) => {
	doc.setFont('helvetica', 'normal');
	doc.setFontSize(fontSize);
	const lines = doc.splitTextToSize(text, doc.internal.pageSize.getWidth() - PAGE.marginX * 2);
	doc.text(lines, PAGE.marginX, y);
	return y + lines.length * PAGE.lineHeight;
};

const getImageFormat = (mimeType: string): 'PNG' | 'JPEG' | 'WEBP' => {
	if (mimeType.includes('png')) return 'PNG';
	if (mimeType.includes('webp')) return 'WEBP';
	return 'JPEG';
};

export const generateBioScorePdf = (report: BioScoreReportData) => {
	const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' }) as PdfDoc;
	let y = PAGE.marginY;

	doc.setFont('helvetica', 'bold');
	doc.setFontSize(20);
	doc.text(report.title, PAGE.marginX, y);
	y += 28;

	doc.setFont('helvetica', 'normal');
	doc.setFontSize(10);
	doc.text(`Generated: ${report.generatedAt}`, PAGE.marginX, y);
	y += 14;
	doc.text(`${report.stage.gate} | ${report.stage.standardsLine}`, PAGE.marginX, y);
	y += 24;

	y = addSectionTitle(doc, 'Building Summary', y);
	const summaryLines = [
		`Average BioScore: ${report.summary.averageBioScore}`,
		`Base average: ${report.summary.baseAverage}`,
		`Noise adjustment: ${report.summary.noiseAdjustment}`,
		`Mode: ${report.summary.mode === 'function_proxy' ? 'Function proxy' : report.summary.mode === 'room' ? 'Room-based' : 'No result data'}`,
		`Rooms included: ${report.summary.roomCount}`,
	];
	summaryLines.forEach((line) => {
		doc.setFont('helvetica', 'normal');
		doc.setFontSize(10);
		doc.text(line, PAGE.marginX, y);
		y += 14;
	});

	y += 8;
	y = ensureSpace(doc, y, 100);
	y = addSectionTitle(doc, 'Stage Context', y);
	y = addWrappedText(doc, report.stage.description, y);

	if (report.noise) {
		y += 12;
		y = ensureSpace(doc, y, 130);
		y = addSectionTitle(doc, 'Noise Summary', y);
		const noiseLines = [
			`Intended final use of this zone: ${report.noise.intendedFinalUse}`,
			`Weighted Score: ${report.noise.weightedScore}`,
			`Target Noise: ${report.noise.targetNoise}`,
			`Exterior Noise: ${report.noise.exteriorNoise}`,
			`Interior Noise: ${report.noise.interiorNoise}`,
		];
		noiseLines.forEach((line) => {
			doc.setFont('helvetica', 'normal');
			doc.setFontSize(10);
			doc.text(line, PAGE.marginX, y);
			y += 14;
		});
		y = addWrappedText(doc, `Recommendation: ${report.noise.recommendation}`, y, 10);
	}

	if (report.roomOverviewRows.length > 0) {
		y = ensureSpace(doc, y + 12, 220);
		y = addSectionTitle(doc, 'Rooms Overview', y + 12);
		autoTable(doc, {
			startY: y,
			theme: 'grid',
			styles: { fontSize: 7, cellPadding: 4 },
			headStyles: { fillColor: [32, 43, 67], textColor: 255 },
			head: [[
				'Function',
				'Room',
				'Room Area',
				'Count',
				'Wall',
				'Floor',
				'Ceiling',
				'Material Source',
			]],
			body: report.roomOverviewRows.map((row) => [
				row.functionName,
				row.roomName,
				row.roomArea,
				String(row.count),
				row.wallMaterial,
				row.floorMaterial,
				row.ceilingMaterial,
				row.materialSource,
			]),
		});
		y = getNextY(doc, y) + 16;
	}

	if (report.resultsRows.length > 0) {
		y = ensureSpace(doc, y + 12, 200);
		y = addSectionTitle(doc, 'BioScore Results', y + 12);
		autoTable(doc, {
			startY: y,
			theme: 'grid',
			styles: { fontSize: 7, cellPadding: 4 },
			headStyles: { fillColor: [32, 43, 67], textColor: 255 },
			head: [[
				'Function',
				'Room',
				'Material',
				'View',
				'Daylight',
				'Bonus',
				'Total',
			]],
			body: report.resultsRows.map((row) => [
				row.functionName,
				row.roomName,
				row.material,
				row.view,
				row.daylight,
				row.bonus,
				row.total,
			]),
		});
		y = getNextY(doc, y) + 16;
	}

	if (report.functionRows.length > 0 && report.resultsRows.length === 0) {
		y = ensureSpace(doc, y + 12, 160);
		y = addSectionTitle(doc, 'Function-Level Proxy Summary', y + 12);
		autoTable(doc, {
			startY: y,
			theme: 'grid',
			styles: { fontSize: 8, cellPadding: 4 },
			headStyles: { fillColor: [32, 43, 67], textColor: 255 },
			head: [['Function', 'Fn Area', 'Palette', 'Proxy Score']],
			body: report.functionRows.map((row) => [
				row.functionName,
				row.functionArea,
				row.paletteName,
				row.proxyScore,
			]),
		});
		y = getNextY(doc, y) + 16;
	}

	const hasBreakdowns =
		report.materialBreakdownRows.length > 0 ||
		report.viewBreakdownRows.length > 0 ||
		report.daylightBreakdownRows.length > 0;

	let breakdownY = PAGE.marginY;
	if (hasBreakdowns) {
		doc.addPage('a4', 'landscape');
		breakdownY = addSectionTitle(doc, 'Detailed Breakdowns', breakdownY);
	}

	if (report.materialBreakdownRows.length > 0) {
		breakdownY = addSectionTitle(doc, 'Materials Breakdown', breakdownY);
		autoTable(doc, {
			startY: breakdownY,
			theme: 'grid',
			styles: { fontSize: 7, cellPadding: 4 },
			headStyles: { fillColor: [95, 111, 82], textColor: 255 },
			head: [[
				'Function',
				'Room',
				'Total',
				'Wall',
				'Floor',
				'Ceiling',
				'Wall Material',
				'Floor Material',
				'Ceiling Material',
				'Source',
				'Recommendation',
			]],
			body: report.materialBreakdownRows.map((row) => [
				row.functionName,
				row.roomName,
				row.total,
				row.wall,
				row.floor,
				row.ceiling,
				row.wallMaterial,
				row.floorMaterial,
				row.ceilingMaterial,
				row.source,
				row.recommendation,
			]),
		});
		breakdownY = getNextY(doc, breakdownY) + 18;
	}

	if (report.viewBreakdownRows.length > 0) {
		breakdownY = ensureSpace(doc, breakdownY, 180);
		breakdownY = addSectionTitle(doc, 'View Breakdown', breakdownY);
		if (report.viewBreakdownRows.some((row) => row.functionName === 'Gate Analysis')) {
			breakdownY = addWrappedText(
				doc,
				'Pre-room analysis (Gate fallback): view scores are based on analysis points/windows not yet assigned to rooms.',
				breakdownY,
				9
			);
			breakdownY += 6;
		}
		autoTable(doc, {
			startY: breakdownY,
			theme: 'grid',
			styles: { fontSize: 7, cellPadding: 4 },
			headStyles: { fillColor: [73, 92, 166], textColor: 255 },
			head: [[
				'Function',
				'Room',
				'Total',
				'Open',
				'Vegetation',
				'Water',
				'Sky View',
				'Diversity',
				'Horizontal',
				'Recommendation',
			]],
			body: report.viewBreakdownRows.map((row) => [
				row.functionName,
				row.roomName,
				row.total,
				row.open,
				row.vegetation,
				row.water,
				row.skyView,
				row.diversity,
				row.horizontal,
				row.recommendation,
			]),
		});
		breakdownY = getNextY(doc, breakdownY) + 18;
	}

	if (report.daylightBreakdownRows.length > 0) {
		breakdownY = ensureSpace(doc, breakdownY, 180);
		breakdownY = addSectionTitle(doc, 'Daylight Breakdown', breakdownY);
		if (report.daylightBreakdownRows.some((row) => row.functionName === 'Gate Analysis')) {
			breakdownY = addWrappedText(
				doc,
				'Pre-room analysis (Gate fallback): sunlight/daylight scores are based on analysis points before room assignment.',
				breakdownY,
				9
			);
			breakdownY += 6;
		}
		autoTable(doc, {
			startY: breakdownY,
			theme: 'grid',
			styles: { fontSize: 7, cellPadding: 4 },
			headStyles: { fillColor: [199, 148, 44], textColor: 255 },
			head: [[
				'Function',
				'Room',
				'Total',
				'Winter',
				'Summer',
				'Spring/Fall',
				'Consistency',
				'Quality',
				'Avg h/day',
				'Recommendation',
			]],
			body: report.daylightBreakdownRows.map((row) => [
				row.functionName,
				row.roomName,
				row.total,
				row.winter,
				row.summer,
				row.springFall,
				row.consistency,
				row.quality,
				row.averageHours,
				row.recommendation,
			]),
		});
		breakdownY = getNextY(doc, breakdownY) + 18;
	}

	const imageGroups = report.images.reduce<Record<string, typeof report.images>>((acc, image) => {
		if (!acc[image.label]) acc[image.label] = [];
		acc[image.label].push(image);
		return acc;
	}, {});

	Object.entries(imageGroups).forEach(([label, images]) => {
		images.forEach((image, index) => {
			doc.addPage('a4', 'landscape');
			let imageY = PAGE.marginY;
			if (index === 0) {
				imageY = addSectionTitle(doc, label, imageY);
			}

			const maxWidth = doc.internal.pageSize.getWidth() - PAGE.marginX * 2;
			const maxHeight = doc.internal.pageSize.getHeight() - imageY - PAGE.marginY;
			const imageWidth = maxWidth;
			const imageHeight = maxHeight;
			doc.addImage(
				image.dataUrl,
				getImageFormat(image.mimeType),
				PAGE.marginX,
				imageY,
				imageWidth,
				imageHeight,
				undefined,
				'FAST'
			);
		});
	});

	const fileDate = new Date().toISOString().slice(0, 10);
	doc.save(`bioscore-report-${fileDate}.pdf`);
};
